import { useMemo, useState, type Dispatch, type SetStateAction } from 'react';
import { format } from 'date-fns';
import type { TFunction } from 'i18next';
import { USAGE_ACTION } from '../constants/sample.constants';
import {
  recalculateRemainingQuantitiesPreservingOrder,
  resolveInitialQuantityFromRecords,
  sumUsedQuantity,
} from '../utils/sampleUsageRecordCalc';
import { applyUsageRecordFieldChange } from '../utils/sampleFormUsageRecordEdit';
import {
  createUsageRecordClientKey,
  type SampleEditUsageRecordRow,
} from '../utils/sampleFormInit';

type UsageRecordFormRow = SampleEditUsageRecordRow;

function usageRecordRowKey(record: UsageRecordFormRow): string {
  return record.clientKey;
}

type Args = {
  t: TFunction;
  sampleCreatedDate: string;
  initialQuantityFallback?: number | null;
  remainingQuantityFallback?: number | null;
  recordedBy: string;
  setError: (message: string) => void;
  setInitialQuantity?: (n: number) => void;
};

/** 編集フォーム内の使用履歴行の追加・更新・削除と残量再計算 */
export function useSampleFormUsageRecords({
  t,
  sampleCreatedDate,
  initialQuantityFallback,
  remainingQuantityFallback,
  recordedBy,
  setError,
  setInitialQuantity,
}: Args): {
  usageRecords: UsageRecordFormRow[];
  setUsageRecords: Dispatch<SetStateAction<UsageRecordFormRow[]>>;
  calculatedRemainingQuantity: number;
  addUsageRecord: () => void;
  removeUsageRecord: (index: number) => void;
  updateUsageRecord: (index: number, field: string, value: unknown) => void;
} {
  const [usageRecords, setUsageRecords] = useState<UsageRecordFormRow[]>([]);

  const calculatedRemainingQuantity = useMemo(() => {
    if (usageRecords.length === 0) {
      return remainingQuantityFallback || 0;
    }
    const initialQuantity = resolveInitialQuantityFromRecords(
      usageRecords,
      initialQuantityFallback ?? 0
    );
    return Math.max(0, initialQuantity - sumUsedQuantity(usageRecords));
  }, [usageRecords, initialQuantityFallback, remainingQuantityFallback]);

  const recalculateRemainingQuantities = (records: UsageRecordFormRow[]) => {
    if (records.length === 0) return records;
    const initialQuantity = resolveInitialQuantityFromRecords(
      records,
      initialQuantityFallback ?? 0
    );
    return recalculateRemainingQuantitiesPreservingOrder(
      records,
      initialQuantity,
      usageRecordRowKey
    );
  };

  const addUsageRecord = () => {
    const newRecord: UsageRecordFormRow = {
      clientKey: createUsageRecordClientKey({}),
      usageDate: format(new Date(), "yyyy-MM-dd'T'HH:mm"),
      action: USAGE_ACTION.USE,
      purpose: '',
      quantity: calculatedRemainingQuantity > 0 ? 1 : 0,
      remainingQuantity: calculatedRemainingQuantity,
      recordedBy,
      isNew: true,
    };
    setUsageRecords([...usageRecords, newRecord]);
  };

  const removeUsageRecord = (index: number) => {
    setUsageRecords(usageRecords.filter((_, i) => i !== index));
  };

  const updateUsageRecord = (index: number, field: string, value: unknown) => {
    const result = applyUsageRecordFieldChange({
      records: usageRecords,
      index,
      field,
      value,
      sampleCreatedDate,
      initialQuantityFallback: initialQuantityFallback ?? undefined,
      enforceQuantityAgainstInitial: initialQuantityFallback != null,
      rowKey: usageRecordRowKey,
      recalculate: recalculateRemainingQuantities,
      messages: {
        usageDateBeforeCreated: t('sample.usage_date_before_created_variant1'),
        quantityExceeds: (remaining) =>
          t('sample.quantity_exceeds_remaining_variant1', { remaining }),
        quantityExceedsAfterRecalc: (quantity, remaining) =>
          t('sample.quantity_exceeds_remaining_variant2', { quantity, remaining }),
      },
    });
    if (result.ok === false) {
      setError(result.error);
      return;
    }
    if (result.initialQuantity !== undefined) {
      setInitialQuantity?.(result.initialQuantity);
    }
    setUsageRecords(result.records);
    setError('');
  };

  return {
    usageRecords,
    setUsageRecords,
    calculatedRemainingQuantity,
    addUsageRecord,
    removeUsageRecord,
    updateUsageRecord,
  };
}
